import { useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  matchConnectionsToEvents,
  matchesQuery,
  metAtStub,
  type Contact,
  type ContactState,
  type MeetSuggestion,
  type OutcomeResult,
} from "@omina/core";
import {
  eventsQuery,
  useContactAvatars,
  useHeadlines,
  useProfiles,
  useContacts,
  useFeeds,
  useLogOutcome,
  useMoves,
  useOutcomes,
  useSettings,
  useUpdateContact,
} from "../lib/queries";
import { ConnectionQueue } from "../components/circle/ConnectionQueue";
import { PersonCard } from "../components/circle/PersonCard";

/**
 * Your circle — everyone in the loop and where each one stands.
 *
 * This list and its outcomes are the moat. Logging is one tap and stays one
 * tap (rule #6): the mutation writes the predicted state into the cache from
 * the same pure reducer the server's transition mirrors, so the row changes
 * under her finger instead of after a round trip.
 */
export const Route = createFileRoute("/circle")({
  component: Circle,
});

const STATE_LABEL: Record<ContactState, { label: string; color: string }> = {
  new: { label: "new", color: "var(--muted-foreground)" },
  contacted: { label: "sent", color: "var(--auth)" },
  replied: { label: "replied", color: "var(--cust)" },
  warm: { label: "meeting", color: "var(--fund)" },
  converted: { label: "converted", color: "var(--cust)" },
};

const OUTCOMES: Array<{ result: OutcomeResult; label: string }> = [
  { result: "replied", label: "Replied" },
  { result: "meeting", label: "Meeting" },
  { result: "converted", label: "Converted" },
  { result: "no_reply", label: "No reply" },
];

function Circle() {
  const contacts = useContacts();
  const moves = useMoves();
  const outcomes = useOutcomes();
  const logOutcome = useLogOutcome();

  const people = contacts.data ?? [];
  const sent = moves.data?.length ?? 0;
  const landed = outcomes.data?.filter((o) => o.result !== "no_reply").length ?? 0;

  // Filtered here rather than by refetching on every keystroke: the whole
  // circle is already in the query cache, so typing is instant and costs
  // nothing. `GET /api/contacts?q=` runs the same predicate in SQL for the
  // day this list outgrows one response.
  const [query, setQuery] = useState("");
  const searching = query.trim().length > 0;
  // Shares one request with the queue's own faces — same endpoint, same
  // cache entry, different half of the response.
  const contactAvatars = useContactAvatars(people.length > 0);
  const profiles = useProfiles(people.length > 0);
  const headlines = useHeadlines(people.length > 0);
  // Her current track, so a fit scored against a goal she has since left can
  // say so instead of quietly reading as current.
  const settings = useSettings();
  const shown = useMemo(
    () => (query.trim() ? people.filter((c) => matchesQuery(c, query)) : people),
    [people, query],
  );

  /** The newest outcome for a contact, so the row can show what she last
   *  told us rather than only the state it produced. */
  function latestOutcome(contactId: string) {
    const ids = new Set(
      (moves.data ?? []).filter((m) => m.contactId === contactId).map((m) => m.id),
    );
    return outcomes.data?.find((o) => ids.has(o.moveId));
  }

  return (
    <>
      <div className="eyebrow">Follow up</div>
      <h1 className="tk">Your circle.</h1>
      <p className="lede">
        Everyone you have met, and what happened next. One tap when something
        comes back — that is what makes every next draft sharper.
      </p>

      <div className="stats">
        <div>
          <b>{people.length}</b>
          <span>People</span>
        </div>
        <div>
          <b>{sent}</b>
          <span>Follow-ups sent</span>
        </div>
        <div>
          <b style={{ color: "var(--cust)" }}>{landed}</b>
          <span>Landed</span>
        </div>
      </div>

      {logOutcome.isError && <div className="danger">{logOutcome.error.message}</div>}

      {/*
        Search sits above everything because of what it is for: she is looking
        for one person she already met, and the queues below are about people
        she has not filed yet. Underneath them it was three screens down —
        past fifty cards — which is not a search box, it is a footnote.

        One field covers all of it. `matchesQuery` reads the name, the company,
        the role, the room she confirmed, that room's address, the labels, and
        the note she typed — so "girl's run", "Equator" and "wants intros to
        LPs" all find the same person without her having to remember which of
        the three she wrote down.
      */}
      {people.length > 0 && (
        <div className="field mt-5.5">
          <input
            className="tk"
            type="search"
            aria-label="Search your circle"
            placeholder="Search a name, a room, an address, a note…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {searching && (
            <p className="hint">
              {shown.length === 0
                ? "Nobody matches that."
                : `${shown.length} of ${people.length}`}
            </p>
          )}
        </div>
      )}

      {/*
        Both queues step aside while she is searching. They are about people
        who are not in the circle yet, so leaving fifty cards between the box
        and its results would answer a question she did not ask.
      */}
      {!searching && (
        <>
          {/* Newly detected LinkedIn connections, each waiting on one tap to
              say which room it was. Above the CSV-derived queue below because
              these are hours old, not months. */}
          <ConnectionQueue />

          <NewConnections people={people} />
        </>
      )}

      {contacts.isLoading ? (
        <p className="lede mt-5.5">
          Reading your circle…
        </p>
      ) : people.length === 0 ? (
        <div className="samplebar mt-5.5">
          Nobody here yet. <Link to="/followup">Add the first person you met →</Link>
        </div>
      ) : (
        <div className="mt-5.5">
          {shown.map((contact) => (
            <PersonCard
              key={contact.id}
              contact={contact}
              avatarUrl={contactAvatars.data?.[contact.id]}
              profileUrl={profiles.data?.[contact.id]}
              headline={headlines.data?.[contact.id]}
              state={STATE_LABEL[contact.state]}
              fit={
                contact.enrichment?.goalFit
                  ? {
                      score: contact.enrichment.goalFit.score,
                      ink: FIT_INK(contact.enrichment.goalFit.score),
                    }
                  : null
              }
              actions={
                // Only once something has actually been sent — before that
                // there is no move for an outcome to hang off, and the API
                // refuses it, correctly.
                contact.state === "new" ? null : (
                  <div className="chips mt-1.5">
                    {OUTCOMES.map((option) => (
                      <button
                        key={option.result}
                        type="button"
                        className="chip"
                        aria-pressed={latestOutcome(contact.id)?.result === option.result}
                        onClick={() =>
                          logOutcome.mutate({ contactId: contact.id, result: option.result })
                        }
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )
              }
            />
          ))}
        </div>
      )}

      <p className="hint">
        <Link to="/followup">+ Add someone you just met</Link>
      </p>
    </>
  );
}

/**
 * "Where did we actually meet?"
 *
 * LinkedIn knows the date a connection was made and nothing about the room.
 * The calendar knows the rooms. Crossing them turns a name into a memory —
 * and a memory is what the next draft hangs on.
 *
 * It proposes; she decides. Nothing is written until she taps, because a
 * stored guess is indistinguishable from a fact three months later. When
 * there is no event near that date, it says so and offers a text box, rather
 * than reaching for the nearest plausible room.
 */
function NewConnections({ people }: { people: Contact[] }) {
  const unplaced = useMemo(
    () => people.filter((c) => c.connectedOn && !c.metAt),
    [people],
  );

  const feeds = useFeeds();
  // Reach back far enough to cover the oldest connection still waiting, so a
  // first sync of an old export can still be placed. Capped at the API's own
  // limit rather than hoping — asking for more silently returns less.
  const pastDays = useMemo(() => {
    if (!unplaced.length) return 30;
    const oldest = unplaced.reduce(
      (min, c) => (c.connectedOn! < min ? c.connectedOn! : min),
      unplaced[0]!.connectedOn!,
    );
    const days = Math.ceil((Date.now() - new Date(oldest).getTime()) / 86_400_000);
    return Math.min(120, Math.max(7, days + 2));
  }, [unplaced]);

  const events = useQuery({
    // Every event in the window, not a rail's worth: a truncated list drops
    // the room she actually met someone in, and nothing would say so.
    ...eventsQuery(pastDays, 1, 200),
    enabled: unplaced.length > 0 && (feeds.data?.length ?? 0) > 0,
  });

  const candidates = useMemo(
    () => matchConnectionsToEvents(unplaced, events.data?.events ?? []),
    [unplaced, events.data],
  );

  if (!unplaced.length) return null;

  return (
    <div className="card mt-5.5">
      <div className="field">
        <span className="lbl">
          {unplaced.length} new connection{unplaced.length === 1 ? "" : "s"} — where did you
          meet?
        </span>
        <p className="hint">
          One tap each. This is what makes the follow-up sound like you were in the room,
          because you were.
        </p>

        {(feeds.data?.length ?? 0) === 0 && (
          <p className="hint">
            Connect a calendar in <Link to="/followup">follow-up</Link> and Omina can
            suggest the room from the date you connected.
          </p>
        )}

        {candidates.slice(0, 12).map((candidate) => (
          <PlaceConnection
            key={candidate.contact.id}
            contact={candidate.contact}
            connectedOn={candidate.connectedOn}
            suggestions={candidate.suggestions}
          />
        ))}
      </div>
    </div>
  );
}

function PlaceConnection({
  contact,
  connectedOn,
  suggestions,
}: {
  contact: Contact;
  connectedOn: string;
  suggestions: MeetSuggestion[];
}) {
  const update = useUpdateContact();
  const [typing, setTyping] = useState(false);
  const [text, setText] = useState("");

  /** One tap writes both: `metAt` is the structured record, `context` is what
   *  the draft prompt actually reads. Splitting them across two actions would
   *  break rule #6 for no gain. */
  function confirm(suggestion: MeetSuggestion) {
    update.mutate({
      id: contact.id,
      metAt: {
        eventTitle: suggestion.title,
        eventDate: suggestion.date,
        eventLocation: suggestion.location ?? null,
        source: "confirmed",
        recordedAt: new Date().toISOString(),
      },
      context: `${metAtStub(suggestion)}${contact.context}`.trim(),
    });
  }

  function saveTyped() {
    const where = text.trim();
    if (!where) return;
    update.mutate({
      id: contact.id,
      metAt: {
        eventTitle: where,
        eventDate: connectedOn,
        source: "manual",
        recordedAt: new Date().toISOString(),
      },
      context: `Met at ${where} (${connectedOn}). ${contact.context}`.trim(),
    });
  }

  /** Skipping is a real answer, and it is what stops the question being asked
   *  every time she opens the page. */
  function skip() {
    update.mutate({
      id: contact.id,
      metAt: { source: "dismissed", recordedAt: new Date().toISOString() },
    });
  }

  return (
    <div className="item">
      <div className="ihead">
        <div>
          <div className="itype">
            {[contact.role, contact.company].filter(Boolean).join(" @ ") || "—"}
          </div>
          <h3>{contact.name}</h3>
          <div className="why">
            {suggestions.length > 0
              ? `Connected ${connectedOn} — that day you were at:`
              : `Connected ${connectedOn}. No calendar events around then.`}
          </div>
        </div>
      </div>

      <div className="chips">
        {suggestions.slice(0, 4).map((suggestion) => (
          <button
            key={`${suggestion.title}-${suggestion.date}`}
            type="button"
            className="chip"
            disabled={update.isPending}
            onClick={() => confirm(suggestion)}
          >
            {suggestion.title}
            {suggestion.dayOffset !== 0 ? ` (${suggestion.date})` : ""}
          </button>
        ))}
        <button type="button" className="chip" onClick={() => setTyping((t) => !t)}>
          Somewhere else
        </button>
        <button type="button" className="chip" disabled={update.isPending} onClick={skip}>
          Skip
        </button>
      </div>

      {typing && (
        <div className="field">
          <input
            className="tk"
            aria-label={`Where you met ${contact.name}`}
            placeholder="a dinner, a DM, an intro from…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && saveTyped()}
          />
          <div className="actions">
            <button type="button" className="btn small" disabled={update.isPending} onClick={saveTyped}>
              Save
            </button>
          </div>
        </div>
      )}

      {update.isError && <div className="danger">{update.error.message}</div>}
    </div>
  );
}


/**
 * The fit meter's ink.
 *
 * Reuses the meter hues rather than inventing a scale: petrol for a strong
 * match, gold for a maybe, muted for a weak one. Never `--primary` as text —
 * ember on paper is 2.6:1 and fails WCAG, which is why `--primary-text`
 * exists and why this uses the three role tokens instead.
 */
function FIT_INK(score: number): string {
  if (score >= 70) return "var(--cust)";
  if (score >= 40) return "var(--auth)";
  return "var(--muted-foreground)";
}

